import { module } from 'modujs'
import { clamp, map } from '../utils/maths'
import gsap from 'gsap'

const BREAKPOINT = 699
const VELOCITY_FACTOR = .5

const SCALING_VALUES = {
    DESKTOP: .2,
    MOBILE: .6
}

export default class extends module {
    constructor(m) {
        super(m)

        // Binding
        this.onResizeEndBind = this.onResizeEnd.bind(this)
        this.onUpdateBind    = this.onUpdate.bind(this)

        // UI
        this.$el           = this.el
        this.$shapes       = this.$('shape')
        this.$introduction = this.$('introduction')[0]
        this.$progressArea = this.$('progressArea')[0]

        // Flags
        this.isMobile = false
        this.isRafPlaying = false
        this.isInview = false
        
        // Animations data
        this.progress = {
            start: 0,
            end: 1,
            current: 0,
            currentSmooth: 0,
            oldSmooth: 0
        }

        this.transform = {
            startTranslate: -1,
            endTranslate: 0,
            currentTranslate: 0,
            startScale: 1,
            endScale: 1,
            currentScale: 1,
            velocity: 0,
            smoothVelocity: 0,
            lerp: .2
        }
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        this.bindEvents()
        this.computeMetrics()
        this.computeTransform()
    }

    destroy() {
        super.destroy()
        this.unbindEvents()
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener('resizeEnd', this.onResizeEndBind)
    }

    unbindEvents() {
        window.removeEventListener('resizeEnd', this.onResizeEndBind)
    }

    ///////////////
    // Callbacks
    ///////////////
    onResizeEnd() {
        requestAnimationFrame(() => {
            this.computeMetrics()
            this.onUpdate()
        })
    }

    onInview(args) {
        this.isInview = args.way === 'enter'

        if (this.isInview) {
            this.play()
        } else {
            this.transform.velocity = 0
        }
    }

    onProgress(progress) {
        this.progress.current = clamp(
            0,
            1,
            map(progress, this.progress.start, this.progress.end, 0, 1)
        )
    }

    onUpdate() {

        // Lerp progress
        this.progress.currentSmooth += (this.progress.current - this.progress.currentSmooth) * this.transform.lerp
        this.progress.currentSmooth = ((1000 * (this.progress.currentSmooth + 0.001)) | 0) / 1000
        
        // Calcul velocity based on smooth progress
        this.transform.velocity = 1000 * (this.progress.currentSmooth - this.progress.oldSmooth)

        // Lerp velocity
        this.transform.smoothVelocity += (this.transform.velocity - this.transform.smoothVelocity) * this.transform.lerp
        this.transform.smoothVelocity = ((1000 * (this.transform.smoothVelocity + 0.001)) | 0) / 1000

        // Set oldSmoothProgress with new value
        this.progress.oldSmooth = this.progress.currentSmooth

        // Compute transform properties
        this.computeTransform()

        // Add delay before stopping raf
        if (!this.isInview && Math.round(this.transform.smoothVelocity) === 0) {
            this.rafIncrement++
            
            if (this.rafIncrement >= 30) {
                this.pause()
            }
        }
    }

    ///////////////
    // Methods
    ///////////////
    computeMetrics() {
        if (window.innerWidth > BREAKPOINT) {
            this.transform.startScale = SCALING_VALUES.DESKTOP

            // Add 50px buffer on each side
            this.transform.startTranslate = this.$introduction.clientWidth / 2 + (0.05 * window.innerWidth)

            // The movement will take place during the whole visuals section
            this.progress.end = 1
        } else {
            this.transform.startScale = SCALING_VALUES.MOBILE

            // Translate shapes outside viewport
            this.transform.startTranslate = window.innerWidth / 2

            // The movement will take place during the blank space between introduction and visuals section
            this.progress.end = 0.6 * window.innerHeight / this.$progressArea.clientHeight
        }
    }

    computeTransform() {
        // Translate
        this.transform.currentTranslate = (1 - this.progress.currentSmooth) * this.transform.startTranslate

        // Scale
        this.transform.currentScale = clamp(
            this.transform.startScale, 
            this.transform.endScale, 
            map(this.progress.currentSmooth, 0, 1, this.transform.startScale, this.transform.endScale)
        )

        let shapeIndex = 0
        while (shapeIndex < this.$shapes.length) {
            
            // Get inertia of the movement, amplified for each shape
            const inertia = this.transform.smoothVelocity * (shapeIndex % (this.$shapes.length / 2)) * VELOCITY_FACTOR
            
            // Intialize item translate
            let itemTranslate = 0

            // Add or Substract inertia value depending on the direction (left / right)
            if (shapeIndex < this.$shapes.length / 2) {
                itemTranslate = - this.transform.currentTranslate - inertia
            } else {
                itemTranslate = this.transform.currentTranslate + inertia
            }
            
            // Apply transform
            this.$shapes[shapeIndex].style.transform = `translate3d(${itemTranslate}px, ${ - inertia}px, 0) scale3d(${this.transform.currentScale}, ${this.transform.currentScale}, 1)`

            shapeIndex++
        }
    }

    // RAF
    ///////////////
    play() {
        if (this.isRafPlaying) return

        this.isRafPlaying = true
        this.rafIncrement = 0

        gsap.ticker.add(this.onUpdateBind)
    }

    pause() {
        if (!this.isRafPlaying) return

        this.isRafPlaying = false

        gsap.ticker.remove(this.onUpdateBind)
    }
}
